import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import _get from 'lodash/get';
import {
  accountSelectors,
  conferenceRemoteModerationSelectors,
  contactSelectors,
  currentUserSelectors,
  meetingsSelectors,
  featureSelectors,
  nucleusConstants,
  remoteModeratorActions,
  directoryActions
} from '@lifesize/nucleus';
import actions from 'actions/Contact';
import { openFullScreenModal } from 'actions/FullScreenModal';
import { endRouteLoading } from 'actions/SideNav';
import Meetings from 'components/Meetings/Meetings';
import Logger from '../../utils/logger';
import { loadInitialMeetings } from 'actions/Contact';
import { routes } from 'constants/Application';
import {
  MEETINGS_FILTER_OPTION_ACTIVE,
  MEETINGS_FILTER_OPTION_MY_MEETINGS
} from 'constants/Contact';
import { CONTACT_MODAL } from 'constants/ContactModal';
import { WEB_RemoteModerators } from 'constants/FeatureFlags';
import { SCHEDULE_MODAL } from 'constants/ScheduleModal';
import {
  permanentMeetingsFeatureSelector,
  permanentMeetingsSettingSelector,
  oneTimeMeetingFeatureSelector,
} from 'selectors/contact';
import { selectPreviousPath } from 'selectors/auth';


/*  Object of action creators (can also be function that returns object).
    Keys will be passed as props to presentational components. Here we are
    implementing our wrapper around increment; the component doesn't care   */

const isRemoteModerationIsActiveForThisMeeting = (rmIsActive, rmCurrentExt, meetingExt) => {
  return rmIsActive && (rmCurrentExt === meetingExt);
};

function mapDispatchToProps(dispatch, ownProps) {
  return {
    fetchNext: () => dispatch(directoryActions.fetchNext(['MEETING'])),
    loadActiveMeetings: () => dispatch(directoryActions.loadActiveMeetings()),
    loadMyMeetings: () => dispatch(directoryActions.loadUserMeetings()),
    handleAudioCall: contact =>
      dispatch(actions.startAudioCall(contact)),
    handleVideoCall: contact =>
      dispatch(actions.startVideoCall(contact)),
    handleChat: contact => {
      ownProps.history.push(routes.chat);
      dispatch(actions.chatWith(contact));
    },
    handleCreateContact: () => dispatch(
      openFullScreenModal(
        CONTACT_MODAL,
        {
          contact: { contactType: nucleusConstants.CONTACT_TYPE_MEETING },
          isCreate: true,
          isMeeting: true
        }
      )
    ),
    handleDelete: contact =>
      dispatch(openFullScreenModal(CONTACT_MODAL, { contact, isMeeting: true, isDelete: true })),
    handleEdit: contact =>
      dispatch(openFullScreenModal(CONTACT_MODAL, { contact, isMeeting: true })),
    handleFilterChange: (type, value) => dispatch(actions.contactsFilterChange(type, value)),
    handleRemoteModerate: async (ext, swap = false) => {
      if (swap || !ext) {
        await dispatch(remoteModeratorActions.leaveCurrent());
      }
      if (ext) {
        dispatch(remoteModeratorActions.join(ext));
      }
    },
    handleSchedule: id => dispatch(openFullScreenModal(SCHEDULE_MODAL, id)),
    handleSelectContact: contact => {
      // CA-1204: Handle loading dependent information if not present
      if (contact && ((contact.moderatorID && !contact.moderator) || (contact.lecturerID && !contact.lecturer))) {
        // reload meeting from server
        Logger.info('Meeting object has missing info, fetching for ID', contact.id);
        dispatch(directoryActions.loadByUUID(contact.id));
      }
    },
    endRouteLoading: () => dispatch(endRouteLoading()),
    loadInitialMeetings: () => dispatch(loadInitialMeetings())
  };
}

const filterValue = state => state.app.contacts.filters.meetings;
const contactsSwitch = createSelector(
  filterValue,
  meetingsSelectors.selectDirectoryMeetings,
  meetingsSelectors.selectDirectoryActiveMeetings,
  meetingsSelectors.selectDirectoryMyMeetings,
  (filter, all, active, personal) => {
    switch (filter) {
      case MEETINGS_FILTER_OPTION_MY_MEETINGS:
        return personal;
      case MEETINGS_FILTER_OPTION_ACTIVE:
        return active;
      default:
        return all;
    }
  }
);

const mapStateToProps = state => {
  const remoteModerationEnabled = featureSelectors.getFeatureFlag(state)(WEB_RemoteModerators);
  const remoteModerationIsActive = conferenceRemoteModerationSelectors.selectIsActive(state);
  const remoteModerationCurrentExt = conferenceRemoteModerationSelectors.selectExtension(state);
  const remoteModerationIsActiveForThisMeeting = (contact) => isRemoteModerationIsActiveForThisMeeting(remoteModerationIsActive, remoteModerationCurrentExt, _get(contact, 'dialString') || _get(contact, 'extension'));
  const showPermanentMeeting = permanentMeetingsFeatureSelector(state);
  const allowPermanentMeeting = permanentMeetingsSettingSelector(state);
  const oneTimeMeetingFeature = oneTimeMeetingFeatureSelector(state);
  const disableMeetingCreation = (!showPermanentMeeting || !allowPermanentMeeting) && !oneTimeMeetingFeature;
  const getSelectedContact = (uuid) => contactSelectors.getContactByUuidGenerator(state)(uuid);
  const isChatEnabled = (contact) => (featureSelectors.isChatEnabled(state)
    && accountSelectors.getAccountFeatures(state).isChatEnabled
    && _get(contact, 'isChattable')
    && _get(contact, 'meetingType') !== 'TEMP'); // temp fix for wrong isChattable value returned for new OTMs (can remove when isChattable is fixed));
  const userCanRemoteModerate = (contact) => (remoteModerationEnabled
    && conferenceRemoteModerationSelectors.selectCanCurrentUserRemoteModerate(state, { extension: _get(contact, 'dialString') || _get(contact, 'extension') }));

  return {
    contacts: contactsSwitch(state),
    currentUserId: currentUserSelectors.getUserUUID(state),
    disableMeetingCreation,
    filter: filterValue(state),
    getSelectedContact,
    isChatEnabled,
    loading: _get(state, 'app.contacts.loadingMeetings'),
    previousPath: selectPreviousPath(state),
    remoteModerationIsActive,
    remoteModerationIsActiveForThisMeeting,
    scrollToSelected: state.app.contacts.scrollToSelected,
    userCanRemoteModerate
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Meetings);
